import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { useParams } from "react-router-dom";
import EventService from "../../../services/EventService";
import "./EventDetails.css";
import LoadingAnimation from "../../Loading/LoadingAnimation";
import { PartnerService } from "../../../services/PartnerService";
import { SpeakerService } from "../../../services/SpeakerService";
import ConfirmationModal from "../../ConfirmationModal/ConfiramtionModal";
import { PROD_IMAGE_URL } from "../../../utils/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import Input from "../../ui/input";
import TextArea from "../../ui/textArea";
import { InstagramIcon, Linkedin } from "lucide-react";

Modal.setAppElement("#root");

const EventDetails = () => {
  const { id: IdEvent } = useParams<{ id: string }>();

  interface Event {
    IdEvent: number;
    EventName: string;
    Location: string;
    StartDate: Date;
    EndDate: Date;
    EventDescription: string;
    EventOverview: string;
    DateCreated: Date;
    Attachments: any[];
    DateDeleted: Date;
    SaveName: string;
    EventBooth: string;
    IdCurrency: string;
  }

  interface Partner {
    IdEventPartner: number;
    IdEvent: number;
    SaveName: string;
    Attachment1: File | null;
  }

  interface Speaker {
    IdEventSpeaker: number;
    IdEvent: number;
    IdSpeaker: number;
    SpeakerName: string;
    SpeakerTitle: string;
    Attachment: File | null;
    SaveName: string | null;
    DateCreated: Date;
    Deleted: number;
    DateDeleted: Date | null;
    SpeakerImage?: string;
    SpeakerBio: string;
    SpeakerInstagram: string;
    SpeakerLinkedIn: string;
  }

  const [event, setEvent] = useState<Event>({
    IdEvent: 0,
    EventName: "",
    Location: "",
    StartDate: new Date(),
    EndDate: new Date(),
    EventDescription: "",
    EventOverview: "",
    DateCreated: new Date(),
    Attachments: [],
    DateDeleted: new Date(),
    SaveName: "",
    EventBooth: "",
    IdCurrency: "",
  });

  const [partner, setPartner] = useState<Partner>({
    IdEventPartner: 0,
    IdEvent: 0,
    SaveName: "",
    Attachment1: null,
  });

  const [selectedPartner, setSelectedPartner] = useState<Partner[]>([]);
  const [partnerToDelete, setPartnerToDelete] = useState<Partner | null>(null);
  const [isNewPartner, setIsNewPartner] = useState(false);
  const [newSaveName, setNewSaveName] = useState("");
  const [newAttachment1, setNewAttachment1] = useState<File | null>(null);
  const [isSavingPartner, setIsSavingPartner] = useState(false);
  const [isCreatingPartner, setIsCreatingPartner] = useState(false);

  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [selectedSpeakers, setSelectedSpeakers] = useState<Speaker[]>([]);
  const [newSpeakerName, setNewSpeakerName] = useState("");
  const [newSpeakerTitle, setNewSpeakerTitle] = useState("");
  const [newSpeakerImage, setNewSpeakerImage] = useState<File | null>(null);
  const [newSpeakerBio, setNewSpeakerBio] = useState("");
  const [newSpeakerInstagram, setNewSpeakerInstagram] = useState("");
  const [newSpeakerLinkedIn, setNewSpeakerLinkedIn] = useState("");
  const [isNewSpeaker, setIsNewSpeaker] = useState(false);
  const [speakerToDelete, setSpeakerToDelete] = useState<Speaker | null>(null);
  const [isCreatingSpeaker, setIsCreatingSpeaker] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<"success" | "error" | null>(
    null
  );
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isSavingEventInfo, setIsSavingEventInfo] = useState(false);
  const [isSavingSpeakers] = useState(false);
  const [curencies, setCurrencies] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const eventResponse = await EventService.getEventById(Number(IdEvent));
        const resCurrencies = await EventService.getCurrencies();
        setCurrencies(resCurrencies.data.My_Result);
        const eventData = eventResponse.data.My_Result.Event;
        setEvent({
          ...eventData,
          StartDate: new Date(eventData.StartDate),
          EndDate: new Date(eventData.EndDate),
        });

        const speakersResponse = await SpeakerService.getAllSpeakers();
        setSpeakers(speakersResponse.data.My_Result);
        const selectedSpeakersData = eventResponse.data.My_Result.Speakers;
        setSelectedSpeakers(selectedSpeakersData || []);

        const partnerResponse = eventResponse.data.My_Result.Partners;

        const partnersData = partnerResponse.map((partner: any) => ({
          SaveName: partner.SaveName,
          IdEventPartner: partner.IdEventPartner,
        }));
        setSelectedPartner(partnersData);
        setMessageType("success");
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }

      setTimeout(() => {
        setMessage(null);
        setMessageType(null);
      }, 3000);
    };
    fetchData();
  }, [IdEvent]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setEvent((prevEvent) => ({ ...prevEvent, [name]: value }));
  };

  const handleSaveEventInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingEventInfo(true);
    try {
      const formattedEvent = {
        ...event,
        StartDate: event.StartDate.toISOString().split("T")[0],
        EndDate: event.EndDate.toISOString().split("T")[0],
      };
      await EventService.updateEvent(formattedEvent);
      setMessage("Event info saved successfully!");
      setMessageType("success");
    } catch (error) {
      console.error("Error saving event info:", error);
      setMessage("Error saving event info, please try again later.");
      setMessageType("error");
    } finally {
      setIsSavingEventInfo(false);
    }

    setTimeout(() => {
      setMessage(null);
      setMessageType(null);
    }, 3000);
  };

  const handleSpeakerSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    if (selectedId === "new") {
      setIsNewSpeaker(true);
    } else {
      setIsNewSpeaker(false);
      const selected = speakers.find(
        (speaker) => speaker.IdSpeaker === Number(selectedId)
      );
      if (selected) {
        setSelectedSpeakers((prev) => [...prev, selected]);
      }
    }
  };

  const handlePartnerSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    if (selectedId === "new") {
      setIsNewPartner(true);
    } else {
      setIsNewPartner(false);
      const selected = selectedPartner.find(
        (partner) => partner.IdEventPartner === Number(selectedId)
      );
      if (selected) {
        setSelectedPartner([selected]);
      }
    }
  };

  const handleSaveEventSpeaker = async (IdSpeaker: number, IdEvent: number) => {
    try {
      const response = await SpeakerService.addEventSpeaker(IdSpeaker, IdEvent);
      if (response && response.status === 200) {
        setMessage("Speaker added successfully.");
        setMessageType("success");
        const addedSpeaker = speakers.find(
          (speaker) => speaker.IdSpeaker === IdSpeaker
        );
        if (addedSpeaker) {
          setSelectedSpeakers((prev) => [...prev, addedSpeaker]);
        }
      }
    } catch (error) {
      console.error("Error adding speaker to event:", error);
      setMessage("Error adding speaker. Please try again later.");
      setMessageType("error");
    }
  };

  const handleSaveEventPartner = async (Attachments: any, IdEvent: number) => {
    try {
      const response = await PartnerService.insertPartner(Attachments, IdEvent);
      if (response && response.status === 200) {
        setMessage("Partner added successfully.");
        setMessageType("success");
        // const addedPartner = partner.find(partner => partner.IdEventPartner === IdEvent);
        // if (addedPartner) {
        //     setSelectedPartner(addedPartner);
        // }
      }
    } catch (error) {
      console.error("Error adding partner to event:", error);
      setMessage("Error adding partner. Please try again later.");
      setMessageType("error");
    }
  };

  const handleRemoveSpeaker = (speaker: Speaker) => {
    if (speaker.IdEventSpeaker) {
      setSpeakerToDelete(speaker);
      setIsConfirmationModalOpen(true);
    } else {
      setSelectedSpeakers((prev) =>
        prev.filter((s) => s.IdSpeaker !== speaker.IdSpeaker)
      );
    }
  };

  const handleRemovePartner = (partner: Partner) => {
    if (partner.IdEventPartner) {
      setPartnerToDelete(partner);
      setIsConfirmationModalOpen(true);
    } else {
      // setSelectedPartner(null);
    }
  };

  const confirmRemovePartner = async () => {
    if (!partnerToDelete) return;

    try {
      const response = await PartnerService.deletePartner(
        partnerToDelete.IdEventPartner
      );
      if (response && response.status === 200) {
        setMessage("Partner removed successfully.");
        setMessageType("success");
        // setSelectedPartner(null);
      }
    } catch (error) {
      console.error("Error removing partner from event:", error);
      setMessage("Error removing partner, please try again later.");
      setMessageType("error");
    } finally {
      setIsConfirmationModalOpen(false);
      setPartnerToDelete(null);
    }
  };

  const confirmRemoveSpeaker = async () => {
    if (!speakerToDelete) return;

    try {
      const response = await SpeakerService.deleteEventSpeaker(
        speakerToDelete.IdEventSpeaker
      );
      if (response && response.status === 200) {
        setMessage("Speaker removed successfully.");
        setMessageType("success");
        setSelectedSpeakers((prev) =>
          prev.filter(
            (speaker) =>
              speaker.IdEventSpeaker !== speakerToDelete.IdEventSpeaker
          )
        );
      }
    } catch (error) {
      console.error("Error removing speaker from event:", error);
      setMessage("Error removing speaker, please try again later.");
      setMessageType("error");
    } finally {
      setIsConfirmationModalOpen(false);
      setSpeakerToDelete(null);
    }
  };

  const handleCreateSpeaker = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreatingSpeaker(true);
    const formData = new FormData();
    formData.append("IdEvent", event.IdEvent.toString());
    formData.append("SpeakerName", newSpeakerName);
    formData.append("SpeakerTitle", newSpeakerTitle);
    formData.append("SpeakerBio", newSpeakerBio);
    formData.append("SpeakerInstagram", newSpeakerInstagram);
    formData.append("SpeakerLinkedIn", newSpeakerLinkedIn);

    if (newSpeakerImage) {
      formData.append("SpeakerImage", newSpeakerImage);
    }
    try {
      const response = await SpeakerService.addSpeaker(formData);
      if (response.status === 200) {
        const newSpeaker = response.data;
        setSpeakers([...speakers, newSpeaker]);
        setIsNewSpeaker(false);
        setMessage("Speaker created successfully!");
        setMessageType("success");
        // Refetch data to update the list of speakers
        const speakersResponse = await SpeakerService.getAllSpeakers();
        setNewSpeakerName("");
        setNewSpeakerTitle("");
        setNewSpeakerImage(null);
        setSpeakers(speakersResponse.data.My_Result);
      } else {
        setMessage("Failed to create speaker.");
        setMessageType("error");
      }
    } catch (error) {
      setMessage("Error creating speaker.");
      setMessageType("error");
    } finally {
      setIsCreatingSpeaker(false);
    }
  };

  // const handleCreatePartner = async (e: React.FormEvent) => {
  //     e.preventDefault();
  //     setIsSavingPartner(true);
  //     const formData = new FormData();
  //     formData.append('IdEvent', event.IdEvent.toString());
  //     formData.append('SaveName', selectedPartner?.SaveName || '');

  //     if (selectedPartner?.Attachment1) {
  //         formData.append('Attachment1', selectedPartner.Attachment1);
  //     }

  //     try {
  //         const response = await PartnerService.insertPartner(formData, event.IdEvent);
  //         if (response.status === 200) {
  //             const newPartner = response.data;
  //             setPartner([...partner, newPartner]);
  //             setSelectedPartner(newPartner);
  //             setMessage('Partner created successfully!');
  //             setMessageType('success');
  //             // Refetch data to update the list of partners
  //             const partnerResponse = await PartnerService.getPartners();
  //             setPartner(partnerResponse.data.My_Result);
  //         } else {
  //             setMessage('Failed to create partner.');
  //             setMessageType('error');
  //         }
  //     } catch (error) {
  //         setMessage('Error creating partner.');
  //         setMessageType('error');
  //     } finally {
  //         setIsSavingPartner(false);
  //     }
  // }

  return (
    <>
      {isLoading ? (
        <div className="loading-container">
          <LoadingAnimation />
        </div>
      ) : (
        <div className="event-details-container">
          {/* Event Information */}
          <div className="event-section">
            <h2>Event Information</h2>
            <form
              onSubmit={handleSaveEventInfo}
              className="flex flex-col space-y-2 w-full ">
              <label>Event Name</label>
              <Input
                type="text"
                name="EventName"
                value={event.EventName}
                onChange={handleInputChange}
              />

              <label>Location</label>
              <Input
                type="text"
                name="Location"
                value={event.Location}
                onChange={handleInputChange}
              />

              <div className="flex">
                <div className="flex-1 space-y-2">
                  <label>Start Date</label>
                  <Input
                    type="date"
                    name="StartDate"
                    value={event.StartDate.toISOString().split("T")[0]}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="flex-1 space-y-2">
                  <label>End Date</label>
                  <Input
                    type="date"
                    name="EndDate"
                    value={event.EndDate.toISOString().split("T")[0]}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <label>Event Hall</label>
              <Input
                type="text"
                name="EventBooth"
                value={event.EventBooth}
                onChange={handleInputChange}
              />

              <label>Event Currency</label>
              <select
                name="IdCurrency"
                className="bg-white border border-gray-300 rounded-2xl py-2 px-3 h-10">
                {curencies.map((currency, index) => (
                  <option key={index} value={currency.IdCurrency}>
                    {currency.CurrencyCode} {currency.Currency}
                  </option>
                ))}
              </select>

              <label>Description</label>
              <TextArea
                name="EventDescription"
                value={event.EventDescription}
                onChange={handleInputChange}></TextArea>

              <label>Overview</label>
              <TextArea
                name="EventOverview"
                value={event.EventOverview}
                onChange={handleInputChange}></TextArea>

              <button
                className="btn w-full !rounded-2xl"
                type="submit"
                disabled={isSavingEventInfo}>
                {isSavingEventInfo ? <LoadingAnimation /> : "Save"}
              </button>
            </form>
          </div>

          {/* Sponsors and Partners */}
          <div className="event-section">
            <h2>Partners</h2>
            <select onChange={handlePartnerSelect}>
              <option value="">Select Partner</option>
              <option value="new">Add New Partner</option>
              {selectedPartner
                .sort((a, b) => a.SaveName.localeCompare(b.SaveName))
                .map((partner) => (
                  <option
                    key={partner.IdEventPartner}
                    value={partner.IdEventPartner}>
                    {partner.SaveName}
                  </option>
                ))}
            </select>

            <div className="partners-list" style={{ textAlign: "center" }}>
              {selectedPartner.map((partner) => (
                <div
                  key={partner.IdEventPartner}
                  className="selected-partner"
                  style={{
                    display: "inline-block",
                    margin: "10px",
                    position: "relative",
                  }}>
                  <div
                    style={{ position: "relative", display: "inline-block" }}>
                    <img
                      src="https://via.placeholder.com/150"
                      alt={partner.SaveName}
                      style={{ display: "block", margin: "0 auto" }}
                    />
                    <button
                      onClick={() => handleRemovePartner(partner)}
                      className="remove-partner-btn">
                      ×
                    </button>
                  </div>
                  <p>{partner.SaveName}</p>
                </div>
              ))}
            </div>

            {isNewPartner && (
              <div className="new-partner-form">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSaveEventPartner(newAttachment1, event.IdEvent);
                  }}>
                  <label>Partner Save Name</label>
                  <Input
                    type="text"
                    value={newSaveName}
                    onChange={(e) => setNewSaveName(e.target.value)}
                  />
                  <label>Partner Attachment</label>
                  <input
                    type="file"
                    onChange={(e) =>
                      setNewAttachment1(e.target.files?.[0] || null)
                    }
                  />
                  <button
                    className="btn"
                    type="submit"
                    disabled={isSavingPartner}>
                    {isSavingPartner ? <LoadingAnimation /> : "Create Partner"}
                  </button>
                </form>
              </div>
            )}

            {!isNewPartner && selectedPartner.length > 0 && (
              <button
                className="btn"
                onClick={() =>
                  selectedPartner.forEach((partner) =>
                    handleSaveEventPartner(partner.Attachment1, event.IdEvent)
                  )
                }
                disabled={isSavingPartner}>
                {isSavingPartner ? <LoadingAnimation /> : "Save All Partners"}
              </button>
            )}
          </div>

          {/* Speakers Section */}
          <div className="event-section">
            <h2>Speakers</h2>
            <select
              onChange={handleSpeakerSelect}
              className="bg-slate-100 border p-2 rounded-2xl w-full">
              <option value="">Select Speaker</option>
              <option value="new">Add New Speaker</option>
              {speakers
                .filter(
                  (speaker) =>
                    !selectedSpeakers.some(
                      (s) => s.IdSpeaker === speaker.IdSpeaker
                    )
                ) // Exclude assigned speakers
                .slice()
                .filter((speaker) => speaker.SpeakerName) // Ensure SpeakerName is defined
                .sort((a, b) => a.SpeakerName.localeCompare(b.SpeakerName))
                .map((speaker) => (
                  <option key={speaker.IdSpeaker} value={speaker.IdSpeaker}>
                    {speaker.SpeakerName}
                  </option>
                ))}
            </select>
            {isNewSpeaker && (
              <div className="new-speaker-form w-full">
                <form
                  onSubmit={handleCreateSpeaker}
                  className="flex flex-col justify space-y-2">
                  <label>Speaker Name</label>
                  <Input
                    type="text"
                    value={newSpeakerName}
                    onChange={(e) => setNewSpeakerName(e.target.value)}
                    required
                  />
                  <label>Speaker Title</label>
                  <Input
                    type="text"
                    value={newSpeakerTitle}
                    onChange={(e) => setNewSpeakerTitle(e.target.value)}
                    required
                  />
                  <label>Speaker Image</label>
                  <Input
                    type="file"
                    className="w-full rounded-2xl p-2 border h-12"
                    onChange={(e) =>
                      setNewSpeakerImage(e.target.files?.[0] || null)
                    }
                    required
                  />

                  <label>Speaker Bio</label>
                  <TextArea
                    value={newSpeakerBio}
                    onChange={(e) => setNewSpeakerBio(e.target.value)}
                    required
                  />
                  <label>Speaker Instagram</label>
                  <Input
                    type="text"
                    value={newSpeakerInstagram}
                    onChange={(e) => setNewSpeakerInstagram(e.target.value)}
                  />
                  <label>Speaker LinkedIn</label>
                  <Input
                    type="text"
                    value={newSpeakerLinkedIn}
                    onChange={(e) => setNewSpeakerLinkedIn(e.target.value)}
                  />

                  <button
                    className="btn w-full !rounded-2xl"
                    type="submit"
                    disabled={isCreatingSpeaker}>
                    {isCreatingSpeaker ? (
                      <LoadingAnimation />
                    ) : (
                      "Create Speaker"
                    )}
                  </button>
                </form>
              </div>
            )}
            <div className="flex flex-wrap space-y-2">
              {selectedSpeakers.map((speaker) => (
                <div
                  key={speaker.IdSpeaker}
                  className="w-full flex items-center flex-col bg-slate-100 p-2 rounded-2xl relative">
                  <img
                    src={`${PROD_IMAGE_URL}${speaker.Attachment}`}
                    alt={speaker.SpeakerName}
                    className="aspect-1/2 w-28"
                  />

                  <p className="text-start w-full">
                    {`${speaker.SpeakerName}`} <br />{" "}
                    {`${speaker.SpeakerTitle}`} <br />
                    {`${speaker.SpeakerBio}`}
                  </p>
                  <div className="flex space-x-2">
                    {speaker.SpeakerInstagram && (
                      <a href={speaker.SpeakerInstagram} target="_blank">
                        <InstagramIcon />
                      </a>
                    )}
                    {speaker.SpeakerLinkedIn && (
                      <a href={speaker.SpeakerLinkedIn} target="_blank">
                        <Linkedin />
                      </a>
                    )}
                  </div>

                  <button
                    className=" absolute top-5 right-5"
                    onClick={() => handleRemoveSpeaker(speaker)}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ))}
            </div>
            {!isNewSpeaker && selectedSpeakers.length > 0 && (
              <button
                className="btn"
                onClick={() =>
                  selectedSpeakers.forEach((speaker) =>
                    handleSaveEventSpeaker(speaker.IdSpeaker, event.IdEvent)
                  )
                }
                disabled={isSavingSpeakers}>
                {isSavingSpeakers ? <LoadingAnimation /> : "Save All Speakers"}
              </button>
            )}
          </div>
        </div>
      )}
      {message && (
        <div className={`message-bar ${messageType}`}>
          {message}
          <button
            className="close-btn"
            onClick={() => {
              setMessage(null);
              setMessageType(null);
            }}>
            ×
          </button>
        </div>
      )}
      {isConfirmationModalOpen && speakerToDelete && (
        <ConfirmationModal
          message="Are you sure you want to delete this speaker?"
          onConfirm={confirmRemoveSpeaker}
          onCancel={() => setIsConfirmationModalOpen(false)}
        />
      )}
      {isConfirmationModalOpen && partnerToDelete && (
        <ConfirmationModal
          message="Are you sure you want to delete this partner?"
          onConfirm={confirmRemovePartner}
          onCancel={() => setIsConfirmationModalOpen(false)}
        />
      )}
    </>
  );
};

export default EventDetails;
